import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getProductVariationsGrouped,
  addProductVariation,
  deleteProductVariation,
  getCanonicalProductNames,
  invalidateVariationCache,
  ProductWithVariations,
} from "@/services/products/productVariationService";
import { toast } from "sonner";

export function useProductVariations() {
  return useQuery<ProductWithVariations[]>({
    queryKey: ["product-variations"],
    queryFn: getProductVariationsGrouped,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useCanonicalProductNames() {
  return useQuery<string[]>({
    queryKey: ["canonical-product-names"],
    queryFn: getCanonicalProductNames,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAddProductVariation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      productId,
      variationName,
    }: {
      productId: string;
      variationName: string;
    }) => addProductVariation(productId, variationName),
    onSuccess: () => {
      invalidateVariationCache();
      queryClient.invalidateQueries({ queryKey: ["product-variations"] });
      toast.success("Variation added successfully");
    },
    onError: (error: Error) => {
      if (error.message.includes("unique_variation_name")) {
        toast.error("This variation already exists");
      } else {
        toast.error(`Failed to add variation: ${error.message}`);
      }
    },
  });
}

export function useDeleteProductVariation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variationId: string) => deleteProductVariation(variationId),
    onSuccess: () => {
      invalidateVariationCache();
      queryClient.invalidateQueries({ queryKey: ["product-variations"] });
      toast.success("Variation deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete variation: ${error.message}`);
    },
  });
}
